/**
 * ChatPanel — pannello chat completo (header, messaggi, suggerimenti,
 * contatti umani, input). ★ Round 2 qualità — estratto da AIChatBubble.jsx.
 * Testid e comportamento invariati.
 */
import React from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import TelegramButton from '../TelegramButton';
import WhatsAppButton from '../WhatsAppButton';
import { SUGGESTED_QUESTIONS } from './useAiChat';

export function ChatPanel({
  messages, sending, input, setInput, sendMessage, onClose, messagesEndRef,
}) {
  const handleSubmit = (e) => {
    e?.preventDefault?.();
    sendMessage();
  };

  return (
    <div
      className="fixed bottom-0 right-0 md:bottom-6 md:right-6 z-[7100] w-full md:w-[400px] h-[100dvh] md:h-[600px] md:max-h-[80vh] bg-[var(--surface-1)] md:rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)] border border-[var(--gp-forest)]/15 flex flex-col animate-in slide-in-from-bottom-4 duration-300"
      role="dialog"
      aria-label="Chat con l'assistente AI"
      data-testid="ai-chat-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 p-4 border-b border-[var(--gp-forest)]/10 bg-[var(--gp-forest)] text-[var(--surface-1)] md:rounded-t-2xl">
        <div className="flex items-center gap-3">
          <div className="relative shrink-0">
            <div className="w-9 h-9 rounded-full bg-[var(--gp-gold)] text-[var(--gp-forest)] flex items-center justify-center font-serif">
              R
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#22c55e] border-2 border-[var(--gp-forest)]" />
          </div>
          <div className="min-w-0">
            <div className="font-serif text-[15px] leading-tight">Assistente Green Paradise</div>
            <div className="text-[10px] tracking-[0.16em] uppercase text-[var(--gp-gold-soft)]">In risposta · online</div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center"
          aria-label="Chiudi chat"
          data-testid="ai-chat-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-5 space-y-3" data-testid="ai-chat-messages">
        {messages.map((m) => (
          <div
            key={m.id}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] md:text-[14px] leading-relaxed whitespace-pre-wrap ${
                m.role === 'user'
                  ? 'bg-[var(--gp-forest)] text-[var(--surface-1)] rounded-br-sm'
                  : 'bg-[var(--gp-forest)]/8 text-[var(--gp-forest)] rounded-bl-sm'
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-[var(--gp-forest)]/8 text-[var(--gp-forest)]/70 rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-[13px] inline-flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Sto rispondendo…
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Suggested questions (only when no user message yet) */}
      {messages.length <= 1 && !sending && (
        <div className="px-4 pb-3 pt-1">
          <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--gp-forest)]/50 mb-2 font-semibold">
            Suggerimenti
          </div>
          <div className="flex flex-wrap gap-1.5">
            {SUGGESTED_QUESTIONS.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => sendMessage(q)}
                className="px-2.5 py-1.5 rounded-full text-[11px] bg-[var(--gp-forest)]/8 hover:bg-[var(--gp-forest)]/15 text-[var(--gp-forest)] transition-colors"
                data-testid={`ai-chat-suggested-${q.slice(0, 12).toLowerCase().replace(/\s+/g, '-')}`}
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="px-4 pt-3 pb-2 border-t border-[var(--gp-forest)]/10 bg-[var(--surface-2,#f6f3ec)] flex flex-col gap-2">
        <span className="text-[10px] tracking-[0.16em] uppercase text-[var(--gp-forest)]/55 font-semibold text-center">
          Preferisci parlare con una persona reale?
        </span>
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <WhatsAppButton variant="solid" size="sm" label="WhatsApp" source="ai-chat-bubble" />
          <TelegramButton variant="solid" size="sm" label="Telegram" source="ai-chat-bubble" />
        </div>
      </div>

      {/* Input */}
      <form
        onSubmit={handleSubmit}
        className="flex items-end gap-2 p-3 border-t border-[var(--gp-forest)]/10 bg-[var(--surface-2,#f6f3ec)] md:rounded-b-2xl"
      >
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
          placeholder="Scrivi qui la tua domanda…"
          rows={1}
          disabled={sending}
          className="flex-1 resize-none bg-[var(--surface-1)] border border-[var(--gp-forest)]/15 rounded-xl px-3 py-2.5 text-[13px] md:text-[14px] text-[var(--gp-forest)] placeholder:text-[var(--gp-forest)]/40 focus:outline-none focus:border-[var(--gp-gold)]/60 max-h-32"
          data-testid="ai-chat-input"
          aria-label="Messaggio per l'assistente AI"
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          className="shrink-0 w-10 h-10 rounded-full bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
          aria-label="Invia messaggio"
          data-testid="ai-chat-send"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>

      <div className="text-[9px] tracking-[0.18em] uppercase text-[var(--gp-forest)]/35 text-center pb-2 px-3 leading-relaxed">
        AI assistant · risposte indicative · per dettagli vincolanti chiama il team
      </div>
    </div>
  );
}
